import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { courses } from '@/mocks/courses';

const plans = [
  {
    name: 'ChatGPT & Prompt Engineering',
    price: '$49',
    originalPrice: '$79',
    period: 'one-time',
    description: 'Master the art of prompting for superhuman results.',
    features: [
      '42 video lessons',
      '6 hours of content',
      'Downloadable prompt templates',
      'Certificate of completion',
      'Lifetime access',
    ],
    cta: 'Enroll Now',
    courseSlug: '/checkout?course=chatgpt-mastery',
    popular: false,
  },
  {
    name: 'Machine Learning Foundations',
    price: '$89',
    originalPrice: '$129',
    period: 'one-time',
    description: 'Rock-solid understanding of ML algorithms and pipelines.',
    features: [
      '78 video lessons',
      '14 hours of content',
      'Hands-on coding projects',
      'Certificate of completion',
      'Lifetime access',
    ],
    cta: 'Enroll Now',
    courseSlug: '/checkout?course=ml-foundations',
    popular: true,
  },
  {
    name: 'AI Agents & Automation',
    price: '$119',
    originalPrice: '$159',
    period: 'one-time',
    description: 'Design and deploy autonomous AI agents.',
    features: [
      '55 video lessons',
      '10 hours of content',
      'Real-world agent projects',
      'Certificate of completion',
      'Lifetime access',
    ],
    cta: 'Enroll Now',
    courseSlug: '/checkout?course=ai-agents',
    popular: false,
  },
  {
    name: 'AI for Developers: APIs to Production',
    price: '$99',
    originalPrice: '$139',
    period: 'one-time',
    description: 'Integrate AI into your apps with confidence.',
    features: [
      '65 video lessons',
      '12 hours of content',
      'Production deployment guide',
      'Certificate of completion',
      'Lifetime access',
    ],
    cta: 'Enroll Now',
    courseSlug: '/checkout?course=ai-for-devs',
    popular: false,
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
              Pricing Plans
            </p>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Choose Your AI Learning Path
            </h1>
            <p className="mt-4 text-base text-foreground-600 max-w-2xl mx-auto">
              Every course includes lifetime access, certificates of completion, and hands-on projects. No subscriptions — pay once, learn forever.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-background-50 border ${
                  plan.popular
                    ? 'border-primary-400 ring-2 ring-primary-200'
                    : 'border-background-200'
                } rounded-lg p-6 flex flex-col transition-all hover:border-primary-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-background-50 text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                  {plan.description}
                </p>

                <div className="mb-4">
                  <span className="text-3xl font-heading font-bold text-foreground-950">{plan.price}</span>
                  <span className="text-sm text-foreground-500 line-through ml-2">{plan.originalPrice}</span>
                  <span className="text-xs text-foreground-500 ml-2">USD</span>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-accent-500 mt-0.5 w-4 h-4 flex items-center justify-center" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.courseSlug}
                  className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none h-10 px-5 text-sm w-full ${
                    plan.popular
                      ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      : 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                  }`}
                >
                  {plan.cta}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center bg-background-100 rounded-lg p-8">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
              Need All Four Courses?
            </h3>
            <p className="text-sm text-foreground-600 mb-4 max-w-md mx-auto">
              Get the complete AI mastery bundle at a discounted price and save over 30%.
            </p>
            <div className="text-3xl font-heading font-bold text-foreground-950 mb-2">
              $249 <span className="text-sm text-foreground-500 line-through ml-2">$356</span>
            </div>
            <p className="text-xs text-foreground-500 mb-6">One-time payment · Lifetime access to all courses</p>
            <Link
              to="/checkout?course=bundle"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-accent-500 text-background-50 hover:bg-accent-600 h-11 px-6 text-sm"
            >
              Get the Bundle
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}